import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { anyPass, equals } from 'ramda'
import {
  Box,
  IconHeart,
  IconHeartActive,
  IconShareArrow,
  Inline,
  Stack
} from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'
import toNumber from 'lodash/toNumber'

import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'
import { formatSize, formatSizeLabel } from '@/utils/format-size'
import { isGroup } from '@/utils/listings'
import useMediaQuery from '@/hooks/use-media-query'
import useRole from '@/hooks/use-role'
import BlurredText from '@/components/blurred-text'
import CurrencyFormatter from '@/components/currency-formatter'
import {
  BathDarkIcon,
  BedroomIcon,
  EntranceIcon,
  FurnishedDarkIcon,
  SizeIcon
} from '@/components/icons'
import { Tooltip } from '@/components/tooltip'
import UnverifiedBadge from '@/components/unverified-badge'
import VerifiedBadge from '@/components/verified-badge'

const isTrue = anyPass([equals('1'), equals(1), equals(true)])

const Wrapper = styled.section`
  display: flex;
  flex-direction: column;
  margin-bottom: 0px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin-top: 4px;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 30px;
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin-bottom: 24px;
  }
`

const DetailsWrapper = styled.div`
  display: grid;
  grid-auto-columns: fit-content;
  grid-auto-flow: column;
  grid-gap: 0;
  background-color: ${colors.background};
  padding: 16px 8px;
  border-radius: 8px;
  flex-wrap: wrap;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    grid-template-columns: repeat(2, 1fr);
    grid-auto-flow: row;
    padding: 8px;
  }
`

const DetailBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 8px 0;

  :not(:last-child) {
    border-right: 1px solid #00000005;

    @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
      border-right: none;

      :nth-child(-n + 2) {
        border-bottom: 1px solid #00000005;
      }
    }
  }

  > svg {
    flex-shrink: 0;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
    justify-content: flex-start;
    padding: 10px 24px;
  }
`

const Address = styled.div`
  display: block;
  color: ${colors.regular};
  font-size: ${fontSizes.medium};
  font-weight: 700;
  line-height: 24px;
  margin: 0;
  word-break: break-word;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 18px;
    line-height: 24px;
  }
`

const DetailText = styled.p`
  color: ${colors.regular};
  font-size: 14px;
  font-weight: ${fontWeights.medium};
  margin: 0 0 0 5px;
`

const PriceWrapper = styled.div`
  display: flex;
  align-content: center;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 6px;
`

const PriceColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`

const PriceStarting = styled.p`
  color: ${colors.regular};
  font-size: 14px;
  font-weight: ${fontWeights.regular};
  margin: 0;
`

const Price = styled.p`
  color: ${props => (props.color ? props.color : colors.regular)};
  font-size: 32px;
  font-weight: ${props => (props.weight ? props.weight : '700')};
  line-height: 40px;
  margin: 0;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 24px;
    line-height: 32px;
  }
`

const Frequency = styled.p`
  color: ${props => (props.color ? props.color : colors.black)};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};
  line-height: 28px;
  margin: 0 0 0px 0px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 18px;
    line-height: 24px;
  }
`

const equalsOne = equals('1')

const formatPrice = (price, locale) => (
  <CurrencyFormatter value={price} locale={locale} />
)

const renderPrice = (listing, unit, t, locale, isListingLocked) => {
  const isRented =
    listing?.state_machine === 'rented' || isTrue(listing?.marked_as_rented)

  const hasPriceDrop =
    listing.previous_price && listing.price_change_status === 'price_down'

  if (isRented) {
    return (
      <PriceWrapper>
        <Price>{t('common.rented', 'Rented')}</Price>
      </PriceWrapper>
    )
  }

  const price = isGroup(unit)
    ? formatPrice(unit.gr_min_price, locale)
    : formatPrice(listing.price, locale)

  return (
    <PriceColumn>
      {isGroup(unit) && (
        <PriceStarting>
          {t('listing:b.listings.view.starting_at.text', 'Starting at')}...
        </PriceStarting>
      )}

      {isListingLocked ? (
        <Inline align="left" alignY="bottom" space="small">
          <BlurredText variant={{ size: 'xlarge' }}>$****</BlurredText>
          <BlurredText variant={{ size: 'large' }}>/ ****</BlurredText>
        </Inline>
      ) : (
        <PriceWrapper>
          {hasPriceDrop && (
            <Price color={vars.foregroundColor.secondary} weight="500">
              <s>{formatPrice(listing.previous_price, locale)}</s>
            </Price>
          )}
          <Price color={hasPriceDrop ? vars.foregroundColor.critical : ''}>
            {price}
          </Price>
          <Frequency color={hasPriceDrop ? vars.foregroundColor.critical : ''}>
            {`/${t(
              `b.listing.view.price_frequency_${listing.price_frequency}.text`
            )}`}
          </Frequency>
        </PriceWrapper>
      )}
    </PriceColumn>
  )
}

const AddressRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
    margin-bottom: 0;
  }
`

const ListingSummary = ({
  furnished,
  listings,
  isStreetNumberHidden,
  building,
  unit,
  isFavourite,
  handleFavourite,
  handleShare,
  areaMeasurementUnit = 'sqft',
  isListingLocked
}) => {
  const { t } = useTranslation()
  const router = useRouter()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const role = useRole()
  const isLandlord = role === 'landlord'
  const listingVerified = listings.verified_state_machine === 'approved'
  const addressVerifiedText = t(
    'b.listing.view.address_verified.tooltip',
    'This listing is address verified.'
  )
  const locale = router?.locale
  const unitScope = unit.unit_type_scope_txt_id
  const areaMeasurementLabel = formatSizeLabel(areaMeasurementUnit)

  const unitNumber =
    unit.unit_number && !equalsOne(unit.hide_unit_number)
      ? unit.unit_number
      : null
  const { full_street_name, street_name, city, state } = building
  const streetName = isStreetNumberHidden ? street_name : full_street_name

  const renderFullTitle = () => (
    <Box
      display="grid"
      alignItems="flexStart"
      style={{ gridTemplateColumns: '1fr max-content' }}
    >
      <Box display="flex" flexDirection="column">
        <Box display="flex" alignItems="center">
          <Address>
            {renderPrice(listings, unit, t, locale, isListingLocked)}
          </Address>
        </Box>
        <Box marginBottom="xxsmall" marginTop="xxxsmall">
          <Stack space="xxsmall">
            <AddressRow>
              {isListingLocked ? (
                <BlurredText variant={{ size: 'large' }}>*****</BlurredText>
              ) : (
                <h1
                  style={{
                    fontWeight: '600',
                    fontSize: '14px',
                    lineHeight: 'unset',
                    margin: 0
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="xxxxsmall"
                    flexWrap="wrap"
                  >
                    {unitNumber && <>{unitNumber} - </>}
                    {streetName},
                    <Box display="flex" alignItems="center" gap="xxxxsmall">
                      {city}, {state}
                      {listingVerified ? (
                        <Tooltip
                          text={addressVerifiedText}
                          position="bottomLeft"
                          left={2}
                          bottom={-1}
                        >
                          <VerifiedBadge
                            verified
                            type="listing"
                            height={16}
                            width={16}
                          />
                        </Tooltip>
                      ) : (
                        <Box display="inlineBlock" marginLeft="xxxsmall">
                          <UnverifiedBadge />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </h1>
              )}
            </AddressRow>
          </Stack>
        </Box>
      </Box>
    </Box>
  )

  const renderMobileTitle = () => (
    <TitleWrapper>
      <Box width="full" display="flex" flexDirection="column" gap="xxsmall">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="spaceBetween"
          overflow="hidden"
          width="full"
        >
          <Address>
            {renderPrice(listings, unit, t, locale, isListingLocked)}
          </Address>
          <Box display="flex" alignItems="center" gap="xxsmall">
            <Box
              border="borderNeutralLight"
              borderRadius="standard"
              as="button"
              padding="xxsmall"
              cursor="pointer"
              onClick={handleShare}
              id="listing-detail-share-click"
            >
              <Box style={{ pointerEvents: 'none' }}>
                <IconShareArrow />
              </Box>
            </Box>
            {!isLandlord && (
              <Box
                border="borderNeutralLight"
                borderRadius="standard"
                as="button"
                padding="xxsmall"
                cursor="pointer"
                onClick={handleFavourite}
                id="listing-details-click-favourite"
              >
                <Box style={{ pointerEvents: 'none' }}>
                  {isFavourite ? (
                    <IconHeartActive size="small" />
                  ) : (
                    <IconHeart size="small" />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <Box>
          <Stack>
            <Box
              display="flex"
              gap="xxxxsmall"
              alignItems="center"
              marginBottom="small"
            >
              {isListingLocked ? (
                <BlurredText variant={{ size: 'large' }}>*****</BlurredText>
              ) : (
                <h1
                  style={{
                    fontWeight: '600',
                    fontSize: '14px',
                    lineHeight: 'unset',
                    margin: 0
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="xxxxsmall"
                    flexWrap="wrap"
                  >
                    {unitNumber && <>{unitNumber} - </>}
                    {streetName},
                    <Box display="flex" alignItems="center" gap="xxxxsmall">
                      {city}, {state}
                      {listingVerified ? (
                        <Tooltip
                          text={addressVerifiedText}
                          position="bottomLeft"
                          left={2}
                          bottom={-1}
                        >
                          <VerifiedBadge
                            verified
                            type="listing"
                            height={16}
                            width={16}
                          />
                        </Tooltip>
                      ) : (
                        <Box display="inlineBlock" marginLeft="xxxsmall">
                          <UnverifiedBadge />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </h1>
              )}
            </Box>
          </Stack>
        </Box>
      </Box>
    </TitleWrapper>
  )

  const getBedrooms = () => {
    if (unitScope === 'single')
      return `${
        formatSize(areaMeasurementUnit, unit.size) || '---'
      } ${areaMeasurementLabel} ${t('common.bedroom', 'Bedroom')}`
    const count = toNumber(unit.count_bedrooms)
    if (count === 0) return t('common.studio')
    return t('listing:b.listing.view.count_bedrooms.text', {
      count,
      defaultValue: '{{count}} Bed',
      defaultValue_plural: '{{count}} Beds'
    })
  }

  const getBathrooms = () => {
    if (unitScope === 'entire')
      return t('listing:b.listing.view.count_bathrooms.text', {
        count: toNumber(unit.count_all_bathrooms),
        defaultValue: '{{count}} Bath',
        defaultValue_plural: '{{count}} Baths'
      })
    const count = toNumber(unit.count_full_bathrooms)
    return t('listing:b.listing.view.count_private_bathrooms.text', {
      count,
      defaultValue: '{{count}} Private Bath'
    })
  }

  const getSize = () => {
    const { gr_min_size, gr_max_size, size } = unit

    const getGroupListingSize = () => {
      if (!gr_min_size) return `--- ${areaMeasurementLabel}`
      const minSize = formatSize(areaMeasurementUnit, gr_min_size)
      const maxSize = formatSize(areaMeasurementUnit, gr_max_size)
      const isSameSize = gr_min_size === gr_max_size
      return isSameSize
        ? `${minSize} ${areaMeasurementLabel}`
        : `${minSize}-${maxSize} ${areaMeasurementLabel}`
    }

    return isGroup(unit)
      ? getGroupListingSize()
      : `${
          formatSize(areaMeasurementUnit, size) || '---'
        } ${areaMeasurementLabel}`
  }

  const renderDetailBoxes = () => (
    <DetailsWrapper>
      <DetailBox>
        <BedroomIcon width={16} height={16} />
        <DetailText>{getBedrooms()}</DetailText>
      </DetailBox>
      {toNumber(unit.count_full_bathrooms) > 0 && (
        <DetailBox>
          <BathDarkIcon width={16} height={16} />
          <DetailText>{getBathrooms()}</DetailText>
        </DetailBox>
      )}
      {unitScope !== 'single' && (
        <DetailBox>
          <SizeIcon width={16} height={16} />
          <DetailText>{getSize()}</DetailText>
        </DetailBox>
      )}
      <DetailBox>
        <FurnishedDarkIcon width={16} height={16} />
        <DetailText>{furnished}</DetailText>
      </DetailBox>

      {equalsOne(unit.private_entrance) && unitScope === 'partial' && (
        <DetailBox>
          <EntranceIcon width={16} height={16} />
          <DetailText>
            {t(
              'listing:b.listing.view.private_entrance.text',
              'Private Entrance'
            )}
          </DetailText>
        </DetailBox>
      )}
    </DetailsWrapper>
  )
  const renderBlurredDetailBoxes = () => (
    <DetailsWrapper>
      <DetailBox>
        <BedroomIcon width={16} height={16} />
        <Box marginLeft="small">
          <BlurredText>* Beds</BlurredText>
        </Box>
      </DetailBox>
      {toNumber(unit.count_full_bathrooms) > 0 && (
        <DetailBox>
          <BathDarkIcon width={16} height={16} />
          <Box marginLeft="small">
            <BlurredText>* Baths</BlurredText>
          </Box>
        </DetailBox>
      )}
      {unitScope !== 'single' && (
        <DetailBox>
          <SizeIcon width={16} height={16} />
          <Box marginLeft="small">
            <BlurredText>* ft&sup2;</BlurredText>
          </Box>
        </DetailBox>
      )}
      <DetailBox>
        <FurnishedDarkIcon width={16} height={16} />
        <Box marginLeft="small">
          <BlurredText>****</BlurredText>
        </Box>
      </DetailBox>
    </DetailsWrapper>
  )

  return (
    <Wrapper>
      <>{isMobile ? renderMobileTitle() : renderFullTitle()}</>
      {isListingLocked ? renderBlurredDetailBoxes() : renderDetailBoxes()}
    </Wrapper>
  )
}

export default ListingSummary
